import React, { useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

interface SeatHoverPopupProps {
    seat: {
        seat_number: string;
        uuid?: string;
        position: { x: number; y: number };
        category?: string;
    };
    row: {
        position: { x: number; y: number };
        row_number: string;
        row_number_position?: string;
        seats: any[];
        uuid?: string;
    };
    zone: {
        name: string;
        position: { x: number; y: number };
        rows: any[];
    };
    ticketData?: {
        seat: any;
        row: any;
        zone: any;
        ticketData?: {
            ticketId: string;
            ticketName: string;
            price: number;
            description?: string;
        } | null;
        orderInfo?: {
            customerName?: string;
            customerEmail?: string;
            customerPhone?: string;
        };
    } | null;
    position: { x: number; y: number };
    ticketColor?: { fill: string; stroke: string };
    seatMapType?: 'client' | 'editor';
}

const SeatHoverPopup: React.FC<SeatHoverPopupProps> = ({
    seat,
    row,
    zone,
    ticketData,
    position,
    ticketColor,
    seatMapType = 'editor'
}) => {
    // Use the passed ticket color or fallback to default green
    const ticketColors = ticketColor || { fill: '#10b981', stroke: '#059669' };
    const popupRef = useRef<HTMLDivElement>(null);
    const [placement, setPlacement] = useState({ left: position.x + 12, top: position.y + 12 });

    useLayoutEffect(() => {
        const el = popupRef.current;
        const width = el?.offsetWidth || 240;
        const height = el?.offsetHeight || 120;
        const margin = 8;
        let left = position.x + 12;
        let top = position.y + 12;
        if (left + width + margin > window.innerWidth) left = position.x - width - 12;
        if (top + height + margin > window.innerHeight) top = position.y - height - 12;
        setPlacement({
            left: Math.max(margin, Math.min(left, window.innerWidth - width - margin)),
            top: Math.max(margin, Math.min(top, window.innerHeight - height - margin)),
        });
    }, [position.x, position.y]);

    return createPortal(
        <div
            ref={popupRef}
            className="fixed bg-white dark:bg-nightElevated rounded shadow-lg border border-gray-200 dark:border-nightBorder pointer-events-none"
            style={{
                left: placement.left,
                top: placement.top,
                width: '240px',
                zIndex: 100001,
            }}
        >
            {/* Seat Information Section */}
            <div className="p-0 grid grid-cols-3 gap-0 mb-0">
                {/* Section */}
                <div className="text-center border-r border-gray-200 dark:border-nightBorder p-2">
                    <div className="text-xs text-gray-500 dark:text-nightMuted uppercase tracking-wide mb-1">SECTION</div>
                    <div className="text-lg font-bold text-black dark:text-nightTextSecondary uppercase truncate" title={zone.name}>
                        {zone.name.length > 6 ? zone.name.split(' ').map(part => part.charAt(0)).join('') : zone.name}
                    </div>
                </div>
                {/* Row */}
                <div className="text-center border-r border-gray-200 dark:border-nightBorder p-2">
                    <div className="text-xs text-gray-500 dark:text-nightMuted uppercase tracking-wide mb-1">ROW</div>
                    <div className="text-lg font-bold text-black dark:text-nightTextSecondary">{row.row_number}</div>
                </div>
                {/* Seat */}
                <div className="text-center p-2">
                    <div className="text-xs text-gray-500 dark:text-nightMuted uppercase tracking-wide mb-1">SEAT</div>
                    <div className="text-lg font-bold text-black dark:text-nightTextSecondary">{seat.seat_number}</div>
                </div>
            </div>

            {/* Price Section */}
            {ticketData?.ticketData && (
                <div
                    className="text-white font-bold px-4 py-3 rounded-b"
                    style={{ backgroundColor: ticketColors.fill }}
                >
                    <div className="flex justify-between items-center mb-1">
                        <div className="text-sm truncate">
                            {ticketData.ticketData.ticketName}
                        </div>
                        <div className="text-lg">
                            ${Number(ticketData.ticketData.price || 0).toFixed(2)}
                        </div>
                    </div>
                    {/* Customer Information for Reserved Seats - Only in Editor Mode */}
                    {seatMapType === 'editor' && ticketData.orderInfo?.customerName && (
                        <div className="text-xs opacity-90 truncate">
                            {ticketData.orderInfo.customerName}
                        </div>
                    )}
                </div>
            )}

            {/* Reserved Seat Customer Info Section - Only when no ticketData but has orderInfo */}
            {!ticketData?.ticketData && ticketData?.orderInfo?.customerName && seatMapType === 'editor' && (
                <div
                    className="text-white font-bold px-4 py-3 rounded-b"
                    style={{ backgroundColor: '#ff4444' }}
                >
                    <div className="text-sm mb-1">RESERVED</div>
                    <div className="text-xs opacity-90 truncate">
                        {ticketData.orderInfo.customerName}
                    </div>
                </div>
            )}
        </div>,
        document.body
    );
};

export default SeatHoverPopup;
